import { useState, useEffect, useRef } from "react";
import MainLayout from "@/components/layout/MainLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { NativeSelect } from "@/components/ui/native-select";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { extractErrorMessage } from "@/lib/errorMessage";
import { Truck, Search, Trash2, PackagePlus } from "lucide-react";

export default function NovaSaidaPage() {
  const [contratos, setContratos] = useState([]);
  const [funcionarios, setFuncionarios] = useState([]);
  const [form, setForm] = useState({
    contrato_id: "", tipo_documento: "saida", data_entrega: "", numero_requisicao: "", endereco_entrega: "",
    contato_obra: "", telefone_obra: "", veiculo_placa: "", condutor_id: "",
    ordem_rota_pos: "", ordem_rota_total: "", origem_tipo: "deposito", origem_contrato_id: "",
  });
  const [linhas, setLinhas] = useState([]);
  const [busca, setBusca] = useState("");
  const [resultados, setResultados] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [emObra, setEmObra] = useState({});

  useEffect(() => {
    if (!form.origem_contrato_id) { setEmObra({}); return; }
    axios.get(`${API}/estoque/em-obra`, { params: { contrato_id: form.origem_contrato_id } })
      .then(r => setEmObra(Object.fromEntries(r.data.map(x => [x.material_id, x.quantidade]))))
      .catch(() => setEmObra({}));
  }, [form.origem_contrato_id]);

  const registrarPerda = async (l) => {
    const saldo = Number(emObra[l.ref_id] || 0);
    const dif = Math.round((saldo - Number(l.quantidade || 0)) * 100) / 100;
    if (dif <= 0) return;
    if (!window.confirm(`Registrar ${dif} x ${l.nome} como Ocorrência de perda/desgaste na obra de origem?`)) return;
    try {
      const r = await axios.post(`${API}/logistica/perda-retornavel`, {
        material_id: l.ref_id, contrato_id: form.origem_contrato_id, quantidade: dif });
      toast.success(r.data.message);
      setEmObra(m => ({ ...m, [l.ref_id]: Math.max(saldo - dif, 0) }));
    } catch (err) { toast.error(extractErrorMessage(err, "Erro ao registrar perda")); }
  };
  const timer = useRef(null);

  useEffect(() => {
    axios.get(`${API}/cronogramas-lookup/contratos-disponiveis`).then(r => setContratos(r.data)).catch(() => {});
    axios.get(`${API}/funcionarios`).then(r => setFuncionarios(r.data)).catch(() => {});
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const selecionarObra = (id) => {
    const c = contratos.find(x => x.contrato_id === id) || {};
    setForm(f => ({ ...f, contrato_id: id, endereco_entrega: c.endereco_obra || "", contato_obra: c.nome_cliente || "", telefone_obra: c.telefone || "" }));
  };

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (!busca.trim()) { setResultados([]); return; }
    timer.current = setTimeout(async () => {
      setBuscando(true);
      try {
        const origem = form.origem_tipo === "obra" && form.origem_contrato_id ? `&origem_contrato_id=${form.origem_contrato_id}` : "";
        const r = await axios.get(`${API}/logistica/busca-itens?q=${encodeURIComponent(busca)}${origem}`);
        setResultados(r.data);
      } catch { setResultados([]); }
      finally { setBuscando(false); }
    }, 350);
  }, [busca, form.origem_tipo, form.origem_contrato_id]);

  const adicionar = (item) => {
    if (item.bloqueio) { toast.error(item.bloqueio); return; }
    if (item.tipo === "patrimonio" && linhas.some(l => l.ref_id === item.ref_id)) {
      toast.error("Este item de patrimônio já está no romaneio (1 por linha)"); return;
    }
    setLinhas(ls => [...ls, { tipo: item.tipo, ref_id: item.ref_id, codigo: item.codigo, nome: item.nome, unidade: item.unidade, quantidade: 1, saldo_disponivel: item.saldo_disponivel }]);
    setBusca(""); setResultados([]);
  };

  const salvar = async () => {
    if (form.tipo_documento !== "devolucao") {
      const fone = (form.telefone_obra || "").replace(/\D/g, "");
      if ((form.endereco_entrega || "").trim().length < 8 || !/\d/.test(form.endereco_entrega)) return toast.error("Informe o endereço completo de entrega (rua, número e bairro)");
      if ((form.contato_obra || "").trim().length < 3) return toast.error("Informe o nome do contato na obra");
      if (fone.length < 8) return toast.error("Informe o telefone do contato (com DDD)");
    }
    setSalvando(true);
    try {
      const payload = {
        ...form,
        ordem_rota_pos: form.ordem_rota_pos ? Number(form.ordem_rota_pos) : null,
        ordem_rota_total: form.ordem_rota_total ? Number(form.ordem_rota_total) : null,
        origem_contrato_id: form.origem_tipo === "obra" ? form.origem_contrato_id : null,
        linhas: linhas.map(l => ({ tipo: l.tipo, ref_id: l.ref_id, quantidade: Number(l.quantidade) || 1, unidade: l.unidade })),
      };
      const r = await axios.post(`${API}/logistica/romaneios`, payload);
      toast.success(`Romaneio ${r.data.numero} criado (Rascunho)`);
      window.location.href = `/logistica/romaneios/${r.data.id}`;
    } catch (err) { toast.error(extractErrorMessage(err, "Erro ao criar romaneio")); }
    finally { setSalvando(false); }
  };

  return (
    <MainLayout>
      <div className="space-y-4 max-w-5xl" data-testid="nova-saida-page">
        <h1 className="text-2xl font-bold text-luvcorp-navy flex items-center gap-2">
          <Truck className="h-6 w-6 text-luvcorp-gold" /> Nova saída para obra
        </h1>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <div className="lg:col-span-2">
              <Label className="text-xs">Tipo de documento</Label>
              <NativeSelect value={form.tipo_documento || "saida"} onValueChange={(v) => {
                setForm(f => ({ ...f, tipo_documento: v, origem_tipo: v === "saida" ? f.origem_tipo : "obra" }));
              }} data-testid="select-tipo-documento"
                options={[
                  { value: "saida", label: "Saída para obra (padrão)" },
                  { value: "devolucao", label: "Devolução avulsa (obra → base)" },
                  { value: "transferencia", label: "Transferência obra → obra" },
                ]} />
            </div>
            <div className="lg:col-span-2">
              <Label className="text-xs">{form.tipo_documento === "devolucao" ? "Destino *" : "Obra / centro de custo *"}</Label>
              <NativeSelect value={form.contrato_id} onValueChange={selecionarObra} data-testid="select-obra" searchable
                placeholder="Selecione a obra (contratos ativos)…"
                options={[
                  { value: "base_escritorio", label: "Base / Escritório (consumo interno / devolução)" },
                  ...contratos.map(c => ({ value: c.contrato_id, label: `${c.numero_contrato} — ${c.titulo} (${c.nome_cliente})` })),
                ]} />
            </div>
            <div>
              <Label className="text-xs">Data da entrega *</Label>
              <Input type="date" min={new Date().toISOString().slice(0, 10)} value={form.data_entrega} onChange={(e) => set("data_entrega", e.target.value)} data-testid="input-data-entrega" />
            </div>
            <div className="lg:col-span-2">
              <Label className="text-xs">Endereço de entrega (rua, número, bairro) *</Label>
              <Input value={form.endereco_entrega} onChange={(e) => set("endereco_entrega", e.target.value)} placeholder="Ex.: Rua das Obras, 120, Botafogo" data-testid="input-endereco" />
            </div>
            <div>
              <Label className="text-xs">Nº da requisição (opcional)</Label>
              <Input value={form.numero_requisicao} onChange={(e) => set("numero_requisicao", e.target.value)} data-testid="input-requisicao" />
            </div>
            <div>
              <Label className="text-xs">Contato na obra (nome) *</Label>
              <Input value={form.contato_obra} onChange={(e) => set("contato_obra", e.target.value)} data-testid="input-contato" />
            </div>
            <div>
              <Label className="text-xs">Telefone *</Label>
              <Input value={form.telefone_obra} onChange={(e) => set("telefone_obra", e.target.value)} placeholder="(21) 99999-9999" data-testid="input-telefone" />
            </div>
            <div>
              <Label className="text-xs">Veículo / placa *</Label>
              <Input value={form.veiculo_placa} onChange={(e) => set("veiculo_placa", e.target.value)} placeholder="Ex.: Fiorino ABC-1D23" data-testid="input-veiculo" />
            </div>
            <div>
              <Label className="text-xs">Condutor (funcionário) *</Label>
              <NativeSelect value={form.condutor_id} onValueChange={(v) => set("condutor_id", v)} data-testid="select-condutor" searchable
                placeholder="Selecione…" options={funcionarios.map(f => ({ value: f.id, label: f.nome }))} />
            </div>
            <div>
              <Label className="text-xs">Ordem na rota</Label>
              <div className="flex items-center gap-2">
                <Input type="number" min="1" value={form.ordem_rota_pos} onChange={(e) => set("ordem_rota_pos", e.target.value)} className="w-16" data-testid="input-ordem-pos" />
                <span className="text-sm text-slate-500">de</span>
                <Input type="number" min="1" value={form.ordem_rota_total} onChange={(e) => set("ordem_rota_total", e.target.value)} className="w-16" data-testid="input-ordem-total" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Origem</Label>
              <NativeSelect value={form.origem_tipo} onValueChange={(v) => set("origem_tipo", v)} data-testid="select-origem"
                options={[{ value: "deposito", label: "Depósito Central (padrão)" }, { value: "obra", label: "Outra obra (transferência obra → obra)" }]} />
            </div>
            {form.origem_tipo === "obra" && (
              <div className="lg:col-span-2">
                <Label className="text-xs">Obra de origem *</Label>
                <NativeSelect value={form.origem_contrato_id} onValueChange={(v) => set("origem_contrato_id", v)} data-testid="select-origem-obra" searchable
                  placeholder="Selecione…"
                  options={contratos.filter(c => c.contrato_id !== form.contrato_id).map(c => ({ value: c.contrato_id, label: `${c.nome_cliente} — ${c.numero_contrato}` }))} />
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 space-y-3">
            <Label className="text-xs">Adicionar itens (materiais do estoque e patrimônio juntos)</Label>
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-3 text-slate-400" />
              <Input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Busque por nome ou código…" className="pl-9" data-testid="input-busca-itens" />
              {(resultados.length > 0 || buscando) && (
                <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-72 overflow-auto">
                  {buscando && <div className="px-3 py-2 text-sm text-slate-400">Buscando…</div>}
                  {resultados.map((it) => (
                    <button key={`${it.tipo}-${it.ref_id}`} onClick={() => adicionar(it)} disabled={!!it.bloqueio}
                      data-testid={`resultado-${it.tipo}-${it.codigo}`}
                      className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-2 border-b border-slate-50 ${it.bloqueio ? "opacity-50 cursor-not-allowed" : "hover:bg-slate-50"}`}>
                      <span>
                        <Badge variant="outline" className={it.tipo === "material" ? "bg-blue-50 text-blue-700 border-blue-300 mr-2" : "bg-amber-50 text-amber-700 border-amber-300 mr-2"}>
                          {it.tipo === "material" ? "MATERIAL" : "PATRIMÔNIO"}
                        </Badge>
                        <b>{it.nome}</b> <span className="text-slate-400">{it.codigo}</span>
                      </span>
                      <span className="text-xs text-slate-500 text-right whitespace-nowrap">
                        {it.tipo === "material" ? `Disponível: ${it.saldo_disponivel}` : (it.bloqueio || it.localizacao || "Disponível")}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-500 border-b">
                  <th className="py-2">Tipo</th><th>Item</th><th>Unid.</th><th>Qtd. enviada</th><th></th>
                </tr>
              </thead>
              <tbody>
                {linhas.length === 0 ? (
                  <tr><td colSpan={5} className="py-6 text-center text-slate-400">Nenhum item. Use a busca acima.</td></tr>
                ) : linhas.map((l, i) => (
                  <tr key={i} className="border-b border-slate-100" data-testid={`linha-item-${i}`}>
                    <td className="py-2">
                      <Badge variant="outline" className={l.tipo === "material" ? "bg-blue-50 text-blue-700 border-blue-300" : "bg-amber-50 text-amber-700 border-amber-300"}>
                        {l.tipo === "material" ? "MATERIAL" : "PATRIMÔNIO"}
                      </Badge>
                    </td>
                    <td className="py-2">{l.nome} <span className="text-slate-400 text-xs">{l.codigo}</span></td>
                    <td className="py-2">{l.unidade}</td>
                    <td className="py-2">
                      {l.tipo === "material" ? (
                        <Input type="number" min="0.01" step="any" value={l.quantidade} data-testid={`input-qtd-${i}`}
                          onChange={(e) => setLinhas(ls => ls.map((x, j) => j === i ? { ...x, quantidade: e.target.value } : x))}
                          className="w-24 h-8" />
                      ) : "1"}
                      {l.tipo === "material" && <span className="text-xs text-slate-400 ml-2">máx {l.saldo_disponivel}</span>}
                      {l.tipo === "material" && form.tipo_documento === "devolucao" && emObra[l.ref_id] != null && (
                        <span className="block text-[11px] text-slate-500 mt-0.5">
                          Em obra: {emObra[l.ref_id]}
                          {Number(l.quantidade || 0) < Number(emObra[l.ref_id]) && (
                            <button onClick={() => registrarPerda(l)} className="ml-2 text-red-600 underline" data-testid={`btn-perda-${i}`}>
                              Registrar {Math.round((Number(emObra[l.ref_id]) - Number(l.quantidade || 0)) * 100) / 100} como perda/desgaste
                            </button>
                          )}
                        </span>
                      )}
                    </td>
                    <td className="py-2 text-right">
                      <Button variant="ghost" size="sm" onClick={() => setLinhas(ls => ls.filter((_, j) => j !== i))} data-testid={`btn-remover-${i}`}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => (window.location.href = "/logistica/romaneios")}>Cancelar</Button>
          <Button onClick={salvar} disabled={salvando || !form.contrato_id || !form.data_entrega || !form.veiculo_placa || !form.condutor_id || !linhas.length}
            className="bg-luvcorp-navy text-white" data-testid="btn-criar-romaneio">
            <PackagePlus className="h-4 w-4 mr-1" /> {salvando ? "Criando…" : "Criar romaneio (Rascunho)"}
          </Button>
        </div>
      </div>
    </MainLayout>
  );
}
